import { inquiryHref } from '@/lib/engagements';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FAQ, { type FAQItem } from '@/components/FAQ';
import Callout from '@/components/Callout';
import {
  Field,
  NumberInput,
  Panel,
  ReadingList,
  Select,
  Stat,
  Table,
  TD,
  TH,
  TextArea,
  ToolIntro,
  ToolSection,
} from '@/components/ToolKit';

import { analyzeTemplates, parseTemplates, WFR_MODEL_VERSION, DEFAULT_COLUMNS, COLUMN_LABELS, type ColumnKey, type ColumnMap, type Delimiter } from '@/lib/tools/wfr-template-model';
import { downloadJson } from '@/lib/tools/download';

const EXAMPLE = `template,supplier,author,last modified
INV_ACME_HDR,"Acme, Inc",Alex,2026-01-01
INV_ACME_LINES,"Acme, Inc",Alex,2026-01-01
INV_NORTHWIND,Northwind Traders,J. Ortiz,2021-11-02
INV_CONTOSO,Contoso Ltd,P. Nair,2024-06-18
INV_FABRIKAM,Fabrikam Inc,J. Ortiz,2017-08-30
INV_GLOBEX,Globex Corporation,,2020-01-15
INV_GENERIC_FALLBACK,,J. Ortiz,2016-05-04
INV_INITECH,Initech,P. Nair,2025-02-11`;

const EXAMPLE_SUPPLIERS = `Acme, Inc
Northwind Traders
Contoso Ltd
Initech
Umbrella Logistics
Stark Components`;

const FAQS: FAQItem[] = [
  {
    q: 'What do I paste?',
    a: 'Use four columns: template, supplier, author and last modified. Commas, tabs and semicolons are supported, including quoted delimiters, escaped quotes and quoted line breaks. Headers are recognized only when all four mappings are unambiguous; otherwise choose the header mode and map the columns. Dates must be YYYY-MM-DD. The active supplier list is one literal name per line.',
  },
  {
    q: 'Does anything leave the browser?',
    a: 'No. The lists are parsed in the page and nothing is stored or transmitted. The analysis of a full project export, described below, is a separate request handled under a written engagement.',
  },
  {
    q: 'What counts as a stale template?',
    a: 'One whose last-modified date is older than the threshold you set, three years by default. Stale is a prompt to check, not a verdict: a template for a supplier whose invoice layout has not changed can be old and correct. Check actual use and retention needs before deciding to retire it.',
  },
  {
    q: 'What does the full analysis add?',
    a: 'The project export itself is read: field definitions, confidence thresholds, validation rules and sample-document references per template. That gives a field coverage matrix, a review of verifier requirements, and an assessment of how cleanly each template’s rules carry forward into either a supported Forms Recognition estate or a different extraction approach. Actual review load needs representative document-level evidence. The analysis is available by request; use the contact form and mention Forms Recognition.',
  },
];

const WfrTemplateAnalyzer = () => {
  const [text, setText] = useState('');
  const [suppliersText, setSuppliersText] = useState('');
  const [staleYears, setStaleYears] = useState(3);

  const [delimiter, setDelimiter] = useState<Delimiter>('auto');
  const [header, setHeader] = useState<'auto' | 'present' | 'absent'>('auto');
  const [columns, setColumns] = useState<ColumnMap>(DEFAULT_COLUMNS);
  // The prerender and first client render both use an empty date. Only the
  // mounted browser chooses "today", so a static build never freezes that date.
  const [referenceDate, setReferenceDate] = useState('');
  useEffect(() => { setReferenceDate(new Date().toISOString().slice(0, 10)); }, []);
  const parsed = useMemo(() => parseTemplates(text, { delimiter, header, columns, referenceDate }), [text, delimiter, header, columns, referenceDate]);
  const templates = parsed.templates;
  const activeSuppliers = useMemo(() => suppliersText.split(/\r?\n/).map(s => s.trim()).filter(Boolean), [suppliersText]);
  const analysis = useMemo(() => analyzeTemplates(templates, activeSuppliers, staleYears, referenceDate), [templates, activeSuppliers, staleYears, referenceDate]);

  const fmt = (d: Date | null, raw: string) => (d ? d.toISOString().slice(0, 10) : raw || '—');

  return (
    <>
      <ToolIntro>
        <p>
          A template list can help inventory supplier-specific configurations in a Forms Recognition project.
          This tool reports recorded authors, dates to review and supplier-name matches. It does not inspect
          recognition logic, learned data or business rules. Paste a template list and, optionally, an active
          supplier list. The tool processes these inputs in the page; it does not upload or save them.
        </p>
      </ToolIntro>

      <ToolSection id="input" title="The template library">
        <Panel>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Field
              label="Templates"
              htmlFor="templates"
              hint="Comma, tab or semicolon separated. Quote fields containing the delimiter or a line break; double any quote inside a quoted field. Dates: YYYY-MM-DD."
            >
              <TextArea
                id="templates"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'template,supplier,author,last modified\nINV_ACME_HDR,"Acme, Inc",Alex,2026-01-01'}
                rows={12}
              />
            </Field>
            <div className="space-y-6">
              <Field label="Active suppliers (optional)" htmlFor="suppliers" hint="One per line, from the ERP supplier master. Names are matched after case and whitespace normalization; aliases are not inferred.">
                <TextArea
                  id="suppliers"
                  value={suppliersText}
                  onChange={(e) => setSuppliersText(e.target.value)}
                  placeholder={'Acme Industrial\nNorthwind Traders'}
                  rows={7}
                />
              </Field>
              <Field label="Stale after (years)" htmlFor="stale" hint="Age is a review prompt, not evidence that a template is defective.">
                <NumberInput
                  id="stale"
                  min={1}
                  max={20}
                  value={staleYears}
                  onChange={(e) => setStaleYears(Math.min(20, Math.max(1, Number(e.target.value) || 1)))}
                />
              </Field>
            </div>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-3">
            <Field label="Delimiter" htmlFor="wfr-delimiter">
              <Select id="wfr-delimiter" value={delimiter} onChange={e => setDelimiter(e.target.value as Delimiter)}>
                <option value="auto">Detect safely</option><option value=",">Comma</option><option value=";">Semicolon</option><option value={'\t'}>Tab</option>
              </Select>
            </Field>
            <Field label="Header row" htmlFor="wfr-header">
              <Select id="wfr-header" value={header} onChange={e => setHeader(e.target.value as typeof header)}>
                <option value="auto">Recognize named columns</option><option value="present">First row is a header; map below</option><option value="absent">No header; map below</option>
              </Select>
            </Field>
            <Field label="Reference date (UTC)" htmlFor="wfr-reference" hint="Set to the browser’s current UTC date after the page loads. You can choose a different review date; analysis requires a valid date.">
              <input className="field" type="date" id="wfr-reference" value={referenceDate} onChange={e => setReferenceDate(e.target.value)} />
            </Field>
          </div>
          {header !== 'auto' && <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map(key => <Field key={key} label={`${COLUMN_LABELS[key]} column`} htmlFor={`wfr-col-${key}`}>
              <Select id={`wfr-col-${key}`} value={columns[key]} onChange={e => setColumns(prev => ({ ...prev, [key]: Number(e.target.value) }))}>
                {Array.from({ length: Math.max(4, parsed.rows[0]?.cells.length ?? 4) }, (_, i) => <option key={i} value={i}>{i + 1}{header === 'present' && parsed.rows[0]?.cells[i] ? ` — ${parsed.rows[0].cells[i]}` : ''}</option>)}
              </Select>
            </Field>)}
          </div>}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              className="btn-outline-forest"
              onClick={() => {
                setText(EXAMPLE);
                setSuppliersText(EXAMPLE_SUPPLIERS);
                setDelimiter('auto'); setHeader('auto'); setColumns(DEFAULT_COLUMNS);
              }}
            >
              Load an example
            </button>
            {(text || suppliersText) && (
              <button
                type="button"
                className="btn-outline-forest"
                onClick={() => {
                  setText('');
                  setSuppliersText(''); setDelimiter('auto'); setHeader('auto'); setColumns(DEFAULT_COLUMNS); setStaleYears(3); setReferenceDate(new Date().toISOString().slice(0, 10));
                }}
              >
                Clear
              </button>
            )}
          </div>
        </Panel>
      </ToolSection>

      {(parsed.errors.length > 0 || parsed.warnings.length > 0) && <ToolSection id="parse-notices" title="Input checks">
        {parsed.errors.length > 0 && <div role="alert"><p className="font-bold">Analysis paused. Correct these input issues:</p><ul className="mt-3 list-disc pl-6">{parsed.errors.map(error => <li key={error}>{error}</li>)}</ul></div>}
        {parsed.warnings.length > 0 && <ul className="mt-3 list-disc pl-6" role="status">{parsed.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>}
      </ToolSection>}
      {templates.length > 0 && <ToolSection id="preview" title="Confirm the parsed columns">
        <p className="mb-4 text-body">First {Math.min(5, templates.length)} parsed rows. Check names, authors and dates before using the analysis. Model {WFR_MODEL_VERSION}; reference {referenceDate}, UTC.</p>
        <Table caption="Parsed template preview"><thead><tr><TH>Template</TH><TH>Supplier</TH><TH>Author</TH><TH>Last modified</TH></tr></thead>
          <tbody>{templates.slice(0, 5).map(t => <tr key={t.line}><TD>{t.name}</TD><TD>{t.supplier || 'Not recorded'}</TD><TD>{t.author || 'Not recorded'}</TD><TD>{t.modifiedRaw || 'Not recorded'} ({t.dateStatus})</TD></tr>)}</tbody>
        </Table>
      </ToolSection>}
      {analysis && (
        <ToolSection id="result" title="Analysis">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" role="status">
            <Stat label="Templates" value={templates.length} note={analysis.noSupplier ? `${analysis.noSupplier} with no supplier` : 'all carry a supplier'} />
            <Stat label="Supplier names listed" value={analysis.suppliers.size} note={`${analysis.multi.length} with more than one template`} />
            <Stat
              label="Top author share"
              value={analysis.topShare === null ? 'Not recorded' : `${analysis.topShare}%`}
              note={analysis.authorRows.length ? `${analysis.authorRows[0].label}, ${analysis.authorRows[0].count} templates` : undefined}
            />
            <Stat label="Older than threshold" value={analysis.stale.length} note={`older than ${staleYears} years${analysis.noDate ? ` · ${analysis.noDate} undated` : ''}`} />
          </div>

          <p className="mt-5 text-body">Date checks: {analysis.noDate} missing, {analysis.invalidDates} invalid, {analysis.futureDates} future. {analysis.noAuthor} rows have no recorded author. Only valid dates at or before {referenceDate} (UTC) are considered for age.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button className="btn-outline-forest" onClick={() => window.print()}>Print</button>
            <button className="btn-outline-forest" onClick={() => downloadJson('wfr-template-analysis.json', { modelVersion: WFR_MODEL_VERSION, referenceDate, timezone: 'UTC', staleYears, delimiter: parsed.delimiter, columns: parsed.columns, templates, warnings: parsed.warnings, activeSuppliers, summary: { ...analysis, suppliers: [...analysis.suppliers.values()] } })}>Download analysis</button>
          </div>
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <div>
              <h3 className="text-small">Recorded authors</h3>
              <div className="mt-3">
                <Table caption="Templates by author">
                  <thead>
                    <tr>
                      <TH>Author</TH>
                      <TH right>Templates</TH>
                      <TH right>Share</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.authorRows.map((a) => (
                      <tr key={a.label}>
                        <TD>{a.label}</TD>
                        <TD right>{a.count}</TD>
                        <TD right>{Math.round((a.count / templates.length) * 100)}%</TD>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
            <div>
              <h3 className="text-small">Dates to review</h3>
              <div className="mt-3">
                {analysis.stale.length ? (
                  <Table caption="Templates older than the stale threshold">
                    <thead>
                      <tr>
                        <TH>Template</TH>
                        <TH>Supplier</TH>
                        <TH>Last modified</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.stale.map((t) => (
                        <tr key={t.line}>
                          <TD strong>{t.name}</TD>
                          <TD>{t.supplier || '—'}</TD>
                          <TD nowrap>{fmt(t.modified, t.modifiedRaw)}</TD>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-body">None older than {staleYears} years.</p>
                )}
              </div>
            </div>
          </div>

          {activeSuppliers.length > 0 && (
            <div className="mt-10">
              <h3 className="text-small">Supplier coverage</h3>
              <div className="mt-3 grid gap-10 lg:grid-cols-2">
                <div>
                  <p className="label">Active names without a supplier-specific match ({analysis.missing.length})</p>
                  {analysis.missing.length ? (
                    <ul className="mt-3 border-t border-rule">
                      {analysis.missing.map((s) => (
                        <li key={s} className="border-b border-rule py-2 text-body">
                          <span className="border-l-[3px] border-l-brass pl-3">{s}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-body">Every active supplier name matches a listed template. Generic-template effectiveness and review rates are not measured here.</p>
                  )}
                </div>
                <div>
                  <p className="label">Templates for suppliers not on the active list ({analysis.orphaned.length})</p>
                  {analysis.orphaned.length ? (
                    <ul className="mt-3 border-t border-rule">
                      {analysis.orphaned.map((s) => (
                        <li key={s.label} className="border-b border-rule py-2 text-body">
                          {s.label} <span className="text-forest/80">({s.count})</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-body">Every template’s supplier is on the active list.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </ToolSection>
      )}

      <ToolSection id="reading" title="How to read the result">
        <ReadingList
          items={[
            {
              title: 'Recorded authors are not current owners',
              body: 'Concentrated authorship is a prompt to identify current maintainers and documentation. Blank authors are reported separately; a historical author field does not establish present ownership.',
            },
            {
              title: 'Older dates prompt review',
              body: 'A stable template can remain correct for years. Check real usage, retention obligations and historical-document needs before retiring a template, even when its supplier is absent from the active list.',
            },
            {
              title: 'Name matching does not measure extraction coverage',
              body: 'Supplier aliases are not reconciled automatically. An unmatched supplier may work well with a generic model. Measure document-level accuracy and review rates before inferring manual work or benefits from a replacement.',
            },
            {
              title: 'Several templates for one supplier deserve a look',
              body: 'Header and line templates are normal. Several variants can be legitimate. Compare their purpose and use before identifying duplicates or retirement candidates.',
            },
          ]}
        />
      </ToolSection>

      <ToolSection id="full" title="The full project analysis">
        <div className="max-w-[46rem]">
          <p className="text-body">
            A project assessment maps configured recognition methods, field definitions, learned examples where used,
            confidence thresholds, validation scripts and ERP business rules. Representative documents and observed
            processing results are needed to measure extraction accuracy and verifier review load. Assess which rules
            can be retained, adapted or rebuilt for the exact target release or extraction approach, then validate
            the result against the same document set.
          </p>
          <Callout kind="decision">
            <p>
              Available by request. Scope and the handling of the export are agreed in writing before anything is sent;
              use the <Link to={inquiryHref('/tools/wfr-template-analyzer')}>contact form</Link> and mention Forms Recognition.
            </p>
          </Callout>
          <p className="text-body">
            Background:{' '}
            <Link to="/notes/oracle-webcenter-forms-recognition-explained" className="link">
              Forms Recognition explained
            </Link>{' '}
            ·{' '}
            <Link to="/migrations/modernize-webcenter-forms-recognition" className="link">
              Forms Recognition options at the 12c boundary
            </Link>{' '}
            ·{' '}
            <Link to="/migrations/wfr-vs-oci-document-understanding" className="link">
              WFR versus OCI Document Understanding
            </Link>
          </p>
        </div>
      </ToolSection>

      <div className="mt-16 max-w-[46rem]">
        <FAQ items={FAQS} />
      </div>
    </>
  );
};

export default WfrTemplateAnalyzer;
